import { Component, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { DeviceManagementService } from 'src/app/device/device-management.service';
import { NotificationMessageService } from 'src/app/shared/notification-message/notification-message.service';
import { SideMenuService } from 'src/app/shared/side-menu/side-menu.service';
import { UsersService } from 'src/app/users/users.service';
import { ModalComponent } from '../../shared/modal/modal.component';
import { TranslateService } from '@ngx-translate/core';
import { ColumnsConfig, TableConfig, TableData} from 'sct-custom-table/sct-table/projects/sct-table/src/lib/custom-table-interface';
import { SCTCustomTable } from 'sct-custom-table/sct-table/projects/sct-table/src/lib/sct-table.component';
import * as moment from 'moment';
import { CommonService, TimeSource } from 'src/app/shared/services/common.service';

@Component({
	selector: 'app-rma-processing',
	templateUrl: './rma-processing.component.html'
})
export class RmaProcessingComponent implements OnInit {
	filter: any = {};
	sort: any = {};

	selectedMacAddress: string = '';;
	selectedType: string = '';
	dialogMsg: string = '';

	/** Which time zone each time column is rendered in. See ST-2326. */
	private readonly timeSourceByColumn: Record<string, TimeSource> = {
		created_at: TimeSource.Utc,
		updated_at: TimeSource.Utc,
	};

	columnConfig: ColumnsConfig[] = [];
	tableData: TableData[] = [];
	tableConfig: TableConfig = {
		hasExport: false,
		hasPagination: true,
		pageSize: 20,
		isBackendPagination: true,
		draggable: true,
		hasActionButtons: true,
		actionButtonsList: [{text: this.translateService.instant('g.export_to_csv'), action: ()=> this.exportToCsvFile(), enable: true}],
	};

	statuses: any  = {
		1: this.translateService.instant('rma.pending'),
		2: this.translateService.instant('rma.fix'),
		3: this.translateService.instant('rma.discard'),
	};

	@ViewChild("confirmDialog") confirmDialog: ModalComponent;
	@ViewChild("sctCustomTable", { static: true }) sctCustomTable!: SCTCustomTable;

	constructor(
		public userService: UsersService,
		private router: Router,
		private sideMenuService: SideMenuService,
		private deviceManagementService: DeviceManagementService,
		private sctToastService: NotificationMessageService,
		private translateService: TranslateService,
		private commonService: CommonService,
	) { }

	ngOnInit() {
		if (!this.userService.hasAccessFunction('shipment_management'))
			return this.router.navigate(['/unauthorized']);

		const statuses = [];
		for (const statusId in this.statuses) {
			statuses.push({label: this.statuses[statusId], value: statusId});
		}
		this.columnConfig = [
			{ key: 'mac_address', name: this.translateService.instant('g.mac_address'), type: "link"},
			{ key: 'serial_number', name: this.translateService.instant('devices.serial_number'), type: "string"},
			{ key: 'po_number', name: this.translateService.instant('g.purchase_order'), type: "string"},
			{ key: 'rma_number', name: this.translateService.instant('devices.rma_no'), type: "string"},
			{ key: 'created_at', name: this.translateService.instant('users.create_date'), type: "date"},
			{ key: 'updated_at', name: this.translateService.instant('rma.updated_at'), type: "date"},
			{ key: 'rma_status', name: this.translateService.instant('g.status'), type: "string", filterType: 'dropdown', items: statuses},
			{ key: 'actions', name: this.translateService.instant('g.actions'), type: "dropdown", hasFilter: false, hasSort: false},
		];
		this.commonService.applyTimeSourceTooltips(this.columnConfig, this.timeSourceByColumn);

		this.sideMenuService.hide();
		this.getRmaDevices({currentPage: 1, isFirstTime: true});
	}

	resetPageVars() {
		this.getRmaDevices({currentPage: 1});
	}

	getRmaDevices(paginationData) {
		if(!this.sctCustomTable)
			return;

		if(paginationData.currentPage == 1)
			this.sctCustomTable.backendPaginationInit();

		const limit = this.sctCustomTable.config.pageSize;

		const options = {
			currentPage: paginationData.currentPage,
			filters: paginationData.filters,
			sort: paginationData.sort,
			limit,
			getCount: paginationData.isFirstTime
		};

		this.filter = options.filters;
		this.sort = options.sort;

		this.deviceManagementService.getRmaDevices(options).subscribe((res: any) => {
			const tableData = [];
			res.data.forEach((log: any) => {
				const optionList = [];
				if (log.rma_status == 1) {
					optionList.push({text: this.translateService.instant('actions.move'), action: (data: any)=> {this.confirmMoveFromRma(data.mac_address.value, 'new')}});
					optionList.push({text: this.translateService.instant('actions.discard'), action: (data: any)=> {this.confirmMoveFromRma(data.mac_address.value, 'discard')}});
				}

				// ST-2409: a soft-deleted device's rma_history rows still surface (LEFT JOIN in
				// getRmaDevices), so its site/customer no longer resolve. Show the MAC as plain
				// text instead of a link into a device page that no longer exists.
				// any: link is added conditionally below: a bare object literal here would infer
				// {value: string} and reject the .link assignment.
				const macAddressCell: any = { value: log.mac_address };
				if (!log.device_deleted)
					macAddressCell.link = ["/", log.customer_id, log.site_id, log.mac_address, 'performance'];

				tableData.push({
					created_at: { value: log.created_at ? moment(log.created_at*1000).utc().format('MM/DD/YYYY HH:mm:ss') : '----' },
					updated_at: { value: log.updated_at ? moment(log.updated_at*1000).utc().format('MM/DD/YYYY HH:mm:ss') : '----' },
					serial_number: { value: log.serial_number },
					po_number: { value: log.po_number },
					rma_number: { value: log.rma_number },
					rma_status: { value: this.statuses[log.rma_status] },
					actions: {value: null, options: optionList},
					customer_id: { value: log.customer_id },
					site_id: { value: log.site_id },
					mac_address: macAddressCell,
				});
			});
			this.tableData = tableData;
			this.sctCustomTable.updatePagination(res.totalDataCount, res.overallItemsCount);
		});
	}

	exportToCsvFile() {
		const options: any = {
			limit: 1000000,
			currentPage: 1,
			getCount: false,
			columns: this.getTableColumn()
		};

		if (this.filter)
			options.filters = this.filter;

		if (this.sort)
			options.sort = this.sort;

		this.deviceManagementService.exportRmaDevicesCSV(options).subscribe((data: any) => {
			const blob = new Blob([data], { type: 'text/csv' });
			const url = window.URL.createObjectURL(blob);
			const a = document.createElement('a');
			a.href = url;
			a.download = `${this.translateService.instant('nav.rma-devices')}.csv`;
			a.click();
			window.URL.revokeObjectURL(url);
		});
	}

	moveFromRma(mac_address: string, type: string) {
		const data = {
			mac_address,
			discard: (type == 'discard')
		}
		this.deviceManagementService.moveFromRma(data).subscribe(() => {
			this.sctToastService.setMessage('globalSuccessMsg');
			this.confirmDialog.hide();
			this.resetPageVars();
		});
	}

	confirmMoveFromRma(mac_address, type: string) {
		this.selectedMacAddress = mac_address;
		this.selectedType = type;
		this.dialogMsg = `rma.${type == 'discard' ? 'discard_body' : 'move_body'}`;
		this.confirmDialog.title = this.translateService.instant(type == 'discard' ? 'rma.discard_header' : 'rma.move_header');
		this.confirmDialog.show();
	}


	getTableColumn() {
		if (this.sctCustomTable)
			return this.sctCustomTable.shownColumnsConfig.map((column) => column.key).filter((key) => key != 'actions');
	}
}
